// src/utils/logger.ts
// ✅ LOGGING: Centralized logging service with environment-aware configuration

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  TRACE = 4
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  service: string;
  message: string;
  data?: any;
  error?: Error;
  sessionId?: string;
  userId?: string;
  tenantId?: string;
}

interface LoggerConfig {
  level: LogLevel;
  enableConsole: boolean;
  bufferSize: number;
  flushInterval: number;
}

class Logger {
  private config: LoggerConfig;
  private sessionId: string;

  constructor(config: Partial<LoggerConfig> = {}) {
    // Environment-aware logging configuration
    const defaultLevel = this.getEnvironmentLogLevel();
    
    this.config = {
      level: defaultLevel,
      enableConsole: true,
      bufferSize: 100,
      flushInterval: 30000,
      ...config
    };

    this.sessionId = this.generateSessionId();
    
    // Only log initialization in development
    if (this.config.level >= LogLevel.DEBUG) {
      console.log(`🟢 Logger initialized with level: ${LogLevel[this.config.level]}`);
    }
  }

  private getEnvironmentLogLevel(): LogLevel {
    // Check if we're in production
    const hostname = window.location.hostname;
    const isProd = hostname.includes('amplifyapp.com') || 
                   hostname.includes('cloudfront.net') ||
                   hostname.includes('qmleo.com') ||
                   process.env.NODE_ENV === 'production';
    
    // Check for explicit log level override via URL param
    const urlParams = new URLSearchParams(window.location.search);
    const debugParam = urlParams.get('debug');
    
    if (debugParam === 'true') {
      return LogLevel.DEBUG; // Force debug mode
    }
    
    if (debugParam === 'false') {
      return LogLevel.WARN; // Force quiet mode
    }
    
    // Default levels by environment
    if (isProd) {
      return LogLevel.WARN; // Production: only warnings and errors
    } else {
      return LogLevel.DEBUG; // Development: everything
    }
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private getUserContext(): { userId?: string; tenantId?: string } {
    try {
      const userId = localStorage.getItem('userId') || undefined;
      const tenantId = localStorage.getItem('tenantId') || undefined;
      return { userId, tenantId };
    } catch {
      return {};
    }
  }

  private createLogEntry(
    level: LogLevel,
    service: string,
    message: string,
    data?: any,
    error?: Error
  ): LogEntry {
    const { userId, tenantId } = this.getUserContext();
    
    return {
      timestamp: new Date().toISOString(),
      level,
      service,
      message,
      data,
      error,
      sessionId: this.sessionId,
      userId,
      tenantId
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.config.level;
  }

  private formatForConsole(entry: LogEntry): void {
    const levelNames = ['ERROR', 'WARN', 'INFO', 'DEBUG', 'TRACE'];
    const levelName = levelNames[entry.level];
    const emoji = ['🔴', '🟡', '🔵', '🟢', '⚪'][entry.level];
    
    const prefix = `${emoji} ${levelName} [${entry.service}]`;
    const timestamp = new Date(entry.timestamp).toLocaleTimeString();
    
    if (entry.level === LogLevel.ERROR && entry.error) {
      console.error(`${prefix} ${timestamp}: ${entry.message}`, entry.data, entry.error);
    } else if (entry.level === LogLevel.WARN) {
      console.warn(`${prefix} ${timestamp}: ${entry.message}`, entry.data);
    } else {
      console.log(`${prefix} ${timestamp}: ${entry.message}`, entry.data);
    }
  }

  private log(
    level: LogLevel,
    service: string,
    message: string,
    data?: any,
    error?: Error
  ): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const entry = this.createLogEntry(level, service, message, data, error);

    // Console logging
    if (this.config.enableConsole) {
      this.formatForConsole(entry);
    }
  }

  // Public logging methods
  error(service: string, message: string, data?: any, error?: Error): void {
    this.log(LogLevel.ERROR, service, message, data, error);
  }

  warn(service: string, message: string, data?: any): void {
    this.log(LogLevel.WARN, service, message, data);
  }

  info(service: string, message: string, data?: any): void {
    this.log(LogLevel.INFO, service, message, data);
  }

  debug(service: string, message: string, data?: any): void {
    this.log(LogLevel.DEBUG, service, message, data);
  }

  trace(service: string, message: string, data?: any): void {
    this.log(LogLevel.TRACE, service, message, data);
  }

  // Service-specific loggers
  createServiceLogger(serviceName: string) {
    return {
      error: (message: string, data?: any, error?: Error) => 
        this.error(serviceName, message, data, error),
      warn: (message: string, data?: any) => 
        this.warn(serviceName, message, data),
      info: (message: string, data?: any) => 
        this.info(serviceName, message, data),
      debug: (message: string, data?: any) => 
        this.debug(serviceName, message, data),
      trace: (message: string, data?: any) => 
        this.trace(serviceName, message, data),
    };
  }

  // Performance timing utilities
  startTimer(service: string, operation: string): () => void {
    const start = performance.now();
    this.debug(service, `Starting ${operation}`);
    
    return () => {
      const duration = performance.now() - start;
      this.info(service, `${operation} completed`, { duration: `${duration.toFixed(2)}ms` });
    };
  }

  // API request logging helper
  logAPIRequest(service: string, method: string, url: string, data?: any): void {
    this.debug(service, `${method} ${url}`, data);
  }

  logAPIResponse(service: string, method: string, url: string, status: number, data?: any): void {
    const level = status >= 400 ? LogLevel.ERROR : status >= 300 ? LogLevel.WARN : LogLevel.DEBUG;
    this.log(level, service, `${method} ${url} → ${status}`, data);
  }

  // Configuration methods
  setLevel(level: LogLevel): void {
    this.config.level = level;
    console.log(`🔧 Log level changed to: ${LogLevel[level]}`);
  }

  // Runtime log level control
  setLogLevel(level: LogLevel): void {
    this.setLevel(level);
  }

  enableDebugMode(): void {
    this.setLogLevel(LogLevel.DEBUG);
  }

  enableProductionMode(): void {
    this.setLogLevel(LogLevel.WARN);
  }

  // Get current configuration
  getLogLevel(): LogLevel {
    return this.config.level;
  }

  getLogLevelName(): string {
    const levelNames = ['ERROR', 'WARN', 'INFO', 'DEBUG', 'TRACE'];
    return levelNames[this.config.level];
  }

  // Cleanup (no-op for now)
  destroy(): void {
    // Nothing to clean up
  }
}

// Create singleton instance
export const logger = new Logger();

// Service-specific loggers for common services
export const authLogger = logger.createServiceLogger('Auth');
export const inventoryLogger = logger.createServiceLogger('Inventory');
export const tenantLogger = logger.createServiceLogger('Tenant');
export const dashboardLogger = logger.createServiceLogger('Dashboard');
export const reportLogger = logger.createServiceLogger('Reports');

// Development helper for easy migration from console.log
export const devLog = {
  log: (service: string, message: string, data?: any) => logger.info(service, message, data),
  error: (service: string, message: string, error?: any) => logger.error(service, message, undefined, error),
  warn: (service: string, message: string, data?: any) => logger.warn(service, message, data),
};

// Global access for debugging (attach to window in development)
if (typeof window !== 'undefined' && !window.location.hostname.includes('amplifyapp.com')) {
  (window as any).logger = logger;
  (window as any).LogLevel = LogLevel;
}
